'use client';

import { useMemo, useState } from 'react';
import NewsCard from './NewsCard';
import { NewsInsight } from '@/lib/news';
import { useAudioPlayerContext } from '@/contexts/AudioPlayerContext';
import { formatDailySummary } from '@/lib/dailySummary';
import { fetchDailyPulse, fetchWeeklyPodcast, parseConversationScript } from '@/lib/marketPulse';

export interface NewsListProps {
  insights: NewsInsight[];
  date: string;
}

export default function NewsList({ insights, date }: NewsListProps) {
  const { isPlaying, isPaused, audioMode, toggle, currentText, play, currentSpeaker } = useAudioPlayerContext();
  const [isLoadingDailyPulse, setIsLoadingDailyPulse] = useState(false);
  const [isLoadingWeeklyPodcast, setIsLoadingWeeklyPodcast] = useState(false);
  const [dailyPulseText, setDailyPulseText] = useState<string | null>(null);
  const [weeklyPodcastText, setWeeklyPodcastText] = useState<string | null>(null);
  const [weeklyPodcastSegments, setWeeklyPodcastSegments] = useState<Array<{ speaker: string; text: string }>>([]);
  
  // Memoize the summary text from storage/news/ data
  const summaryText = useMemo(
    () => formatDailySummary(insights, date),
    [insights, date]
  );
  
  // Check if global audio is active
  const isGlobalPlaying = isPlaying && audioMode === 'global' && currentText === summaryText;
  const isGlobalPaused = isPaused && audioMode === 'global' && currentText === summaryText;
  
  // Check if daily pulse is playing
  const isDailyPulsePlaying = isPlaying && currentText === dailyPulseText && audioMode === 'card';
  const isDailyPulsePaused = isPaused && currentText === dailyPulseText && audioMode === 'card';
  
  // Check if weekly podcast is playing
  const isWeeklyPodcastPlaying = isPlaying && currentText === weeklyPodcastText && audioMode === 'card';
  const isWeeklyPodcastPaused = isPaused && currentText === weeklyPodcastText && audioMode === 'card';

  const handleDailySummaryClick = () => {
    // Daily summary already includes its own greeting, so we don't need withGreeting here
    // Numbers will be preprocessed automatically (default behavior)
    toggle(summaryText, { mode: 'global', preprocessNumbers: true });
  };
  
  const handleDailyPulseClick = async () => {
    if (dailyPulseText && (isDailyPulsePlaying || isDailyPulsePaused)) {
      // Toggle play/pause if already loaded
      toggle(dailyPulseText, { mode: 'card', preprocessNumbers: true });
      return;
    }
    
    setIsLoadingDailyPulse(true);
    try {
      const response = await fetchDailyPulse(date, 'Dubai');
      setDailyPulseText(response.script);
      play(response.script, { mode: 'card', preprocessNumbers: true });
    } catch (error) {
      console.error('Error fetching daily pulse:', error);
      alert('Failed to load Daily Market Pulse. Please ensure the backend is running.');
    } finally {
      setIsLoadingDailyPulse(false);
    }
  };
  
  const handleWeeklyPodcastClick = async () => {
    if (weeklyPodcastText && (isWeeklyPodcastPlaying || isWeeklyPodcastPaused)) {
      // Toggle play/pause if already loaded
      toggle(weeklyPodcastText, { 
        mode: 'card', 
        preprocessNumbers: true,
        isConversation: weeklyPodcastSegments.length > 0,
        segments: weeklyPodcastSegments
      });
      return;
    }
    
    setIsLoadingWeeklyPodcast(true);
    try {
      const response = await fetchWeeklyPodcast(date, 'Dubai');
      setWeeklyPodcastText(response.script);
      
      // Parse conversation if it's a two-person format
      let segments: Array<{ speaker: string; text: string }> = [];
      if (response.format === 'two_person_conversation' || response.script.includes('SARAH:') || response.script.includes('MIKE:')) {
        segments = parseConversationScript(response.script);
        setWeeklyPodcastSegments(segments);
      }
      
      // Play with conversation mode if segments exist
      play(response.script, { 
        mode: 'card', 
        preprocessNumbers: true,
        isConversation: segments.length > 0,
        segments: segments
      });
    } catch (error) {
      console.error('Error fetching weekly podcast:', error);
      alert('Failed to load Weekly Market Podcast. Please ensure the backend is running.');
    } finally {
      setIsLoadingWeeklyPodcast(false);
    }
  };

  if (!insights || insights.length === 0) {
    return (
      <div className="news-list__empty">
        <p>No news available for {date}</p>
      </div>
    );
  }

  return (
    <section className="news-list">
      <div className="news-list__header">
        <div className="news-list__header-content">
          <time className="news-list__date" dateTime={date}>
            {new Date(date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </time>
          <div className="news-list__button-group">
            <button
              className="news-list__daily-summary-button"
              onClick={handleDailySummaryClick}
              aria-label={
                isGlobalPlaying ? 'Pause daily summary' : 
                isGlobalPaused ? 'Resume daily summary' : 
                'Todays News Summary'
              }
              title={
                isGlobalPlaying ? 'Pause daily summary' : 
                isGlobalPaused ? 'Resume daily summary' : 
                'Todays News Summary'
              }
            >
              {isGlobalPlaying ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M5.5 3.5A.5.5 0 0 1 6 4v8a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5zm5 0A.5.5 0 0 1 11 4v8a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5z"/>
                  </svg>
                  <span>Pause</span>
                </>
              ) : isGlobalPaused ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
                  </svg>
                  <span>Resume</span>
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
                  </svg>
                  <span>Todays News Summary</span>
                </>
              )}
            </button>
            
            <button
              className="news-list__market-pulse-button"
              onClick={handleDailyPulseClick}
              disabled={isLoadingDailyPulse}
              aria-label={
                isDailyPulsePlaying ? 'Pause daily market pulse' : 
                isDailyPulsePaused ? 'Resume daily market pulse' : 
                'Daily Market Pulse'
              }
              title="Daily Market Pulse - Real estate market update"
            >
              {isLoadingDailyPulse ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" className="spinning">
                    <path d="M8 0a8 8 0 1 0 8 8A8 8 0 0 0 8 0zm0 2a6 6 0 1 1-6 6 6 6 0 0 1 6-6z" opacity="0.3"/>
                    <path d="M8 0a8 8 0 0 1 8 8h-2a6 6 0 0 0-6-6V0z">
                      <animateTransform attributeName="transform" type="rotate" from="0 8 8" to="360 8 8" dur="1s" repeatCount="indefinite"/>
                    </path>
                  </svg>
                  <span>Loading...</span>
                </>
              ) : isDailyPulsePlaying ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M5.5 3.5A.5.5 0 0 1 6 4v8a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5zm5 0A.5.5 0 0 1 11 4v8a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5z"/>
                  </svg>
                  <span>Pause</span>
                </>
              ) : isDailyPulsePaused ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
                  </svg>
                  <span>Resume</span>
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
                  </svg>
                  <span>Daily Market Pulse</span>
                </>
              )}
            </button>
            
            <button
              className="news-list__market-pulse-button news-list__weekly-podcast-button"
              onClick={handleWeeklyPodcastClick}
              disabled={isLoadingWeeklyPodcast}
              aria-label={
                isWeeklyPodcastPlaying ? 'Pause weekly podcast' : 
                isWeeklyPodcastPaused ? 'Resume weekly podcast' : 
                'Weekly Market Podcast'
              }
              title="Weekly Market Podcast - Two hosts discuss the 7-day market summary"
            >
              {isLoadingWeeklyPodcast ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" className="spinning">
                    <path d="M8 0a8 8 0 1 0 8 8A8 8 0 0 0 8 0zm0 2a6 6 0 1 1-6 6 6 6 0 0 1 6-6z" opacity="0.3"/>
                    <path d="M8 0a8 8 0 0 1 8 8h-2a6 6 0 0 0-6-6V0z">
                      <animateTransform attributeName="transform" type="rotate" from="0 8 8" to="360 8 8" dur="1s" repeatCount="indefinite"/>
                    </path>
                  </svg>
                  <span>Loading...</span>
                </>
              ) : isWeeklyPodcastPlaying ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M5.5 3.5A.5.5 0 0 1 6 4v8a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5zm5 0A.5.5 0 0 1 11 4v8a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5z"/>
                  </svg>
                  <span>{currentSpeaker ? `${currentSpeaker} speaking...` : 'Pause'}</span>
                </>
              ) : isWeeklyPodcastPaused ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
                  </svg>
                  <span>Resume</span>
                </>
              ) : (
                <>
                  {/* Podcast/conversation icon */}
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M8 1a5 5 0 0 0-5 5v1h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a6 6 0 1 1 12 0v6a2.5 2.5 0 0 1-2.5 2.5H9.366a1 1 0 0 1-.866.5h-1a1 1 0 1 1 0-2h1a1 1 0 0 1 .866.5H11.5A1.5 1.5 0 0 0 13 12h-1a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h1V6a5 5 0 0 0-5-5z"/>
                  </svg>
                  <span>Weekly Podcast</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <div className="news-list__grid">
        {insights.map((insight) => (
          <NewsCard key={insight.slug} {...insight} date={date} />
        ))}
      </div>
    </section>
  );
}

